import { useState } from "react";
import { useDashboardStats } from "@/lib/hooks/useDashboardStats";
import { useConversionsAll } from "@/lib/hooks/useConversions";
import { useGroups } from "@/lib/hooks/useGroups";
import { useTenant } from "@/lib/hooks/useTenant";
import { useLinks } from "@/lib/hooks/useLinks";
import { SetupWizard } from "@/components/onboarding/SetupWizard";
import { SetupChecklist } from "@/components/dashboard/SetupChecklist";
import { useOnboardingState } from "@/lib/hooks/useOnboarding";
import { shouldShowOnboarding } from "@lucralink/shared";
import { cn } from "@/lib/utils";
import { type Period } from "@/lib/period";
import { Link } from "@tanstack/react-router";
import {
  Users,
  LinkIcon,
  MousePointerClick,
  AlertCircle,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  Clock,
  Eye,
  BarChart2,
  CheckCircle2,
  XCircle,
  MinusCircle,
} from "lucide-react";
import { Card, CardLabel } from "@/components/ui/card";
import { Sparkline } from "@/components/dashboard/Sparkline";
import { BarChart, AxisLabels } from "@/components/dashboard/BarChart";
import { DonutChart, DonutLegend } from "@/components/dashboard/DonutChart";
import { GreetingBanner } from "@/components/dashboard/GreetingBanner";
import { FeaturedLinks } from "@/components/dashboard/FeaturedLinks";
import { DealRadar } from "@/components/dashboard/DealRadar";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(n: number): string {
  return n.toLocaleString("pt-BR");
}

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `há ${diff}s`;
  if (diff < 3600) return `há ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
  return `há ${Math.floor(diff / 86400)}d`;
}

const MARKETPLACE_LABELS: Record<string, string> = {
  shopee: "Shopee",
  amazon: "Amazon",
  mercadolivre: "Mercado Livre",
  magazine_luiza: "Magalu",
  americanas: "Americanas",
};

function mkLabel(code: string | null): string {
  if (!code) return "Outro";
  return MARKETPLACE_LABELS[code.toLowerCase()] ?? code;
}

// Labels de periodo para exibicao no cabecalho
const PERIOD_LABELS: Record<Period, string> = {
  today: "Hoje",
  "7d": "7 dias",
  "30d": "30 dias",
};

const PERIOD_SUBTITLE: Record<Period, string> = {
  today: "hoje",
  "7d": "últimos 7 dias",
  "30d": "últimos 30 dias",
};

// Cores para plataformas nos graficos
const PLATFORM_COLORS: Record<string, string> = {
  amazon: "hsl(42 80% 57%)",
  mercadolivre: "hsl(200 70% 50%)",
  shopee: "hsl(330 70% 55%)",
  magalu: "hsl(150 54% 51%)",
  aliexpress: "hsl(270 60% 60%)",
  awin: "hsl(0 0% 50%)",
  shein: "hsl(340 80% 55%)",
};

function platformColor(code: string): string {
  return PLATFORM_COLORS[code.toLowerCase()] ?? "hsl(0 0% 60%)";
}

// ---------------------------------------------------------------------------
// EmptyChart
// ---------------------------------------------------------------------------

function EmptyChart(): JSX.Element {
  return (
    <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-[hsl(var(--border-strong))] bg-[hsl(var(--surface)/0.45)] px-4 text-center">
      <p className="text-xs leading-relaxed text-muted-foreground">
        Sem dados no período selecionado.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

function CardSkeleton({ lines = 2 }: { lines?: number }): JSX.Element {
  return (
    <div className="animate-pulse space-y-3" aria-busy="true">
      <div className="h-2.5 w-20 rounded bg-[hsl(var(--border-strong))]" />
      <div className="h-8 w-16 rounded bg-[hsl(var(--border-strong))]}" />
      {lines > 1 && <div className="h-2 w-12 rounded bg-[hsl(var(--border-strong)/0.6)]" />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DeltaBadge
// ---------------------------------------------------------------------------

interface DeltaBadgeProps {
  delta: number | null;
  className?: string;
}

function DeltaBadge({ delta, className }: DeltaBadgeProps): JSX.Element {
  if (delta === null) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-xs font-semibold",
          "bg-[hsl(var(--border-strong))] text-muted-foreground",
          className,
        )}
      >
        novo
      </span>
    );
  }

  const isPositive = delta >= 0;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 font-mono text-xs font-semibold",
        isPositive
          ? "bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))]"
          : "bg-[hsl(var(--destructive)/0.12)] text-[hsl(var(--destructive))]",
        className,
      )}
      title={`${isPositive ? "+" : ""}${delta}% vs período anterior`}
    >
      {isPositive ? <ArrowUpRight size={11} aria-hidden="true" /> : <ArrowDownRight size={11} aria-hidden="true" />}
      {isPositive ? "+" : ""}{delta}%
    </span>
  );
}

// ---------------------------------------------------------------------------
// MetricCard - card de metrica compacto (estilo easyfy)
// ---------------------------------------------------------------------------

interface MetricCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  delta?: number | null;
  subtitle?: string;
  isLoading?: boolean;
  className?: string;
}

function MetricCard({ label, value, icon, delta, subtitle, isLoading, className }: MetricCardProps): JSX.Element {
  return (
    <Card variant="raised" className={cn("p-4 flex flex-col gap-2", className)}>
      {isLoading ? (
        <CardSkeleton lines={2} />
      ) : (
        <>
          <div className="flex items-center justify-between">
            <CardLabel>{label}</CardLabel>
            <div className="h-7 w-7 rounded-lg bg-[hsl(var(--primary)/0.1)] flex items-center justify-center">
              {icon}
            </div>
          </div>
          <p className="text-2xl font-bold tabular-nums text-foreground leading-none">
            {value}
          </p>
          <div className="flex items-center gap-2">
            {delta !== undefined && delta !== null && (
              <DeltaBadge delta={delta} />
            )}
            {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
          </div>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PeriodSelector
// ---------------------------------------------------------------------------

interface PeriodSelectorProps {
  value: Period;
  onChange: (p: Period) => void;
}

const PERIODS: Period[] = ["today", "7d", "30d"];

function PeriodSelector({ value, onChange }: PeriodSelectorProps): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Selecionar período"
      className="flex items-center rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] p-0.5 gap-0.5"
    >
      {PERIODS.map((p) => {
        const active = p === value;
        return (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            aria-pressed={active}
            className={cn(
              "rounded-md px-3 py-1 text-xs font-medium transition-all duration-[120ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-1",
              active
                ? "bg-[hsl(var(--surface-raised))] text-foreground shadow-sm border border-[hsl(var(--border-strong))]"
                : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-raised)/0.5)] active:scale-95",
            )}
          >
            {PERIOD_LABELS[p]}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gera labels de datas para os eixos
// ---------------------------------------------------------------------------

function buildDateLabels(startDate: Date, days: number): string[] {
  return Array.from({ length: days }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  });
}

// ---------------------------------------------------------------------------
// HorizontalBarChart - barras horizontais para Links por Plataforma
// ---------------------------------------------------------------------------

interface BarItem {
  label: string;
  value: number;
  color: string;
}

function HorizontalBarChart({ items }: { items: BarItem[] }): JSX.Element {
  const max = Math.max(...items.map((i) => i.value), 1);
  const total = items.reduce((s, i) => s + i.value, 0) || 1;

  return (
    <div className="flex flex-col gap-3">
      {items.map((item, i) => {
        const pct = Math.round((item.value / total) * 100);
        const barPct = Math.round((item.value / max) * 100);
        return (
          <div key={i} className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: item.color }}
                  aria-hidden="true"
                />
                <span className="text-xs text-foreground font-medium">{item.label}</span>
              </div>
              <span className="text-xs font-mono tabular-nums text-muted-foreground">
                {item.value.toLocaleString("pt-BR")} ({pct}%)
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-[hsl(var(--border-strong))]">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${barPct}%`,
                  backgroundColor: item.color,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DashboardPage
// ---------------------------------------------------------------------------

export function DashboardPage(): JSX.Element {
  const [period, setPeriod] = useState<Period>("30d");

  const { data: stats, isLoading: statsLoading, error: statsError } = useDashboardStats(period);
  const { data: conversions, isLoading: convLoading } = useConversionsAll();
  const { data: groups } = useGroups();
  const { data: tenantBilling } = useTenant();
  const { data: onboarding } = useOnboardingState();
  const { data: allLinks = [], isLoading: linksLoading } = useLinks();

  const tenant = tenantBilling?.tenant ?? null;

  // Wizard de 5 passos so para conta nova (linha aberta em onboarding_progress);
  // retoma do passo salvo no banco.
  if (tenant && onboarding?.progress && shouldShowOnboarding(onboarding.progress)) {
    return <SetupWizard tenantId={tenant.id} tenantName={tenant.name ?? ""} progress={onboarding.progress} />;
  }

  // Metricas derivadas
  const activeGroups = stats?.activeGroups ?? 0;
  const totalLinks = stats?.totalLinks ?? 0;
  const clicksToday = stats?.clicksToday ?? 0;
  const clicksInPeriod = stats?.clicksThisMonth ?? 0;
  const dailyClicks = stats?.dailyClicks ?? [];
  const hasDailyClicks = dailyClicks.some((v) => v > 0);

  // Conversoes com sucesso
  const successConversions = stats?.successConversions ?? 0;
  const dailySuccessConversions = stats?.dailySuccessConversions ?? [];
  const hasDailySuccess = dailySuccessConversions.some((v) => v > 0);

  // Taxa de sucesso
  const statusCounts = stats?.conversionStatusCounts ?? { success: 0, error: 0, skipped: 0, pending: 0, total: 0 };
  const successDenominator = statusCounts.success + statusCounts.error;
  const successRate = successDenominator > 0
    ? ((statusCounts.success / successDenominator) * 100).toFixed(1)
    : null;

  // Deltas
  const deltas = stats?.deltas ?? { successConversions: null, clicks: null };

  // Labels de datas para os graficos
  const periodDays = period === "today" ? 1 : period === "7d" ? 7 : 30;
  const periodStart = (() => {
    const d = new Date();
    d.setDate(d.getDate() - periodDays + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  const dateLabels = buildDateLabels(periodStart, periodDays);

  // Ultima conversao
  const lastConversion = conversions?.find((c) => c.status === "success") ?? conversions?.[0] ?? null;

  // Cliques por plataforma (para grafico de pizza)
  const platformClickMap: Record<string, number> = {};
  if (conversions) {
    for (const c of conversions) {
      if (c.marketplace_code) {
        platformClickMap[c.marketplace_code] = (platformClickMap[c.marketplace_code] ?? 0) + 1;
      }
    }
  }
  const platformClickEntries = Object.entries(platformClickMap)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);
  const donutSlices = platformClickEntries.map(([code, count]) => ({
    label: mkLabel(code),
    value: count,
    color: platformColor(code),
  }));

  // Links por plataforma (para grafico de barras)
  const linksByPlatform: Record<string, number> = {};
  for (const link of allLinks) {
    if (link.marketplace_code) {
      linksByPlatform[link.marketplace_code] = (linksByPlatform[link.marketplace_code] ?? 0) + 1;
    }
  }
  const linksPlatformEntries = Object.entries(linksByPlatform)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);
  const barItems = linksPlatformEntries.map(([code, count]) => ({
    label: mkLabel(code),
    value: count,
    color: platformColor(code),
  }));

  const groupsNearLimit = groups?.filter((g) => g.status !== "active").length ?? 0;
  const isEmptyState = !statsLoading && !statsError && activeGroups === 0;

  const viewsCount = clicksInPeriod;
  const clicksCount = clicksToday;
  const linksCount = totalLinks;

  return (
    <div className="space-y-6 max-w-6xl">

      {/* Erro de carregamento */}
      {statsError && (
        <div className="flex items-center gap-3 rounded-lg border border-[hsl(var(--destructive)/0.3)] bg-[hsl(var(--destructive)/0.08)] px-4 py-3 text-sm text-[hsl(var(--destructive))]">
          <AlertCircle size={16} className="shrink-0" />
          <span>Erro ao carregar métricas: {statsError.message}</span>
        </div>
      )}

      <SetupChecklist dismissed={onboarding?.checklistDismissed ?? true} />

      {/* CABECALHO: saudacao + seletor de periodo (estilo easyfy) */}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <GreetingBanner name={tenant?.name ?? null} />
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {/* 4 METRICAS PRINCIPAIS (estilo easyfy) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Visualizações"
          value={fmt(viewsCount)}
          icon={<Eye size={14} className="text-[hsl(var(--primary))]" />}
          delta={deltas.clicks}
          subtitle={PERIOD_SUBTITLE[period]}
          isLoading={statsLoading}
        />
        <MetricCard
          label="Cliques"
          value={fmt(clicksCount)}
          icon={<MousePointerClick size={14} className="text-[hsl(var(--primary))]" />}
          subtitle="hoje"
          isLoading={statsLoading}
        />
        <MetricCard
          label="Taxa de Conversão"
          value={successRate !== null ? `${successRate}%` : "---"}
          icon={<CheckCircle2 size={14} className="text-[hsl(var(--primary))]" />}
          delta={deltas.successConversions}
          subtitle={successRate !== null ? `vs anterior` : "sem dados"}
          isLoading={statsLoading}
        />
        <MetricCard
          label="Links Gerados"
          value={fmt(linksCount)}
          icon={<LinkIcon size={14} className="text-[hsl(var(--primary))]" />}
          isLoading={statsLoading}
        />
      </div>

      {/* Dica contextual: grupos conectados mas sem conversoes ainda */}
      {!isEmptyState && !statsLoading && !statsError && activeGroups > 0 && successConversions === 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-[hsl(var(--gold)/0.25)] bg-[hsl(var(--gold)/0.06)] px-4 py-3 text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0 text-[hsl(var(--gold))]" />
          <div className="space-y-0.5">
            <p className="font-medium text-foreground">Grupos conectados, aguardando conversões</p>
            <p className="text-muted-foreground">
              O bot já está monitorando seus grupos. Configure as credenciais de cada marketplace em{" "}
              <Link to="/app/account" className="underline hover:text-foreground">
                Minha conta
              </Link>{" "}
              para que os links sejam convertidos automaticamente.
            </p>
          </div>
        </div>
      )}

      {/* GRAFICO PRINCIPAL + PIE CHART */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">

        {/* Visualizacoes de Paginas (timeline) - estilo easyfy */}
        <Card variant="revenue" className="lg:col-span-3 p-5 flex flex-col gap-4 relative overflow-hidden grain-overlay">
          <div
            className="pointer-events-none absolute inset-0 rounded-xl"
            style={{
              background: "radial-gradient(ellipse at 80% 20%, hsl(42 80% 57% / 0.06) 0%, transparent 65%)",
            }}
            aria-hidden="true"
          />

          <div className="flex items-center justify-between relative z-10">
            <div>
              <CardLabel>Visualizações de Páginas</CardLabel>
              <p className="text-xs text-muted-foreground mt-0.5 capitalize">{PERIOD_SUBTITLE[period]}</p>
            </div>
            <div className="flex items-center gap-3">
              {deltas.clicks !== undefined && deltas.clicks !== null && (
                <DeltaBadge delta={deltas.clicks} />
              )}
              <span className="label-editorial text-[hsl(var(--gold))]">
                {fmt(clicksInPeriod)} no período
              </span>
            </div>
          </div>

          {statsLoading ? (
            <div className="h-24 animate-pulse rounded bg-[hsl(var(--border-strong)/0.3)]" />
          ) : hasDailyClicks ? (
            <div className="relative pt-2 z-10">
              <BarChart
                values={dailyClicks}
                labels={dateLabels}
                className="h-20"
                color="hsl(43 96% 56%)"
              />
              <AxisLabels labels={dateLabels} />
            </div>
          ) : (
            <EmptyChart />
          )}

          <div className="flex items-center gap-1.5 pt-1 border-t border-[hsl(var(--border))] relative z-10">
            <div className="h-2 w-2 rounded-sm bg-[hsl(var(--gold))]" aria-hidden="true" />
            <span className="text-[11px] text-muted-foreground">
              Série calculada a partir de click_events.clicked_at.
            </span>
          </div>
        </Card>

        {/* Cliques por Plataforma (grafico de pizza) - estilo easyfy */}
        <Card variant="raised" className="p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <CardLabel>Cliques por Plataforma</CardLabel>
            <BarChart2 size={14} className="text-muted-foreground" />
          </div>

          {convLoading ? (
            <div className="animate-pulse space-y-3">
              <div className="mx-auto h-28 w-28 rounded-full bg-[hsl(var(--border-strong)/0.5)]" />
              <div className="h-3 w-24 mx-auto rounded bg-[hsl(var(--border-strong))]" />
            </div>
          ) : donutSlices.length > 0 ? (
            <div className="flex flex-col items-center gap-4">
              <DonutChart slices={donutSlices} size={100} strokeWidth={20} />
              <DonutLegend slices={donutSlices} className="w-full" />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <BarChart2 size={24} className="text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Sem dados de clique por plataforma</p>
            </div>
          )}
        </Card>
      </div>

      {/* RADAR DE PRODUTOS RASTREADOS (Diferencial Competitivo LucraLink) */}
      <DealRadar
        links={allLinks}
        isLoading={linksLoading}
      />

      {/* LINKS POR PLATAFORMA + LINKS EM DESTAQUE */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">

        {/* Links Gerados por Plataforma (grafico de barras horizontais) */}
        <Card variant="raised" className="lg:col-span-2 p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <CardLabel>Links Gerados por Plataforma</CardLabel>
            <LinkIcon size={14} className="text-muted-foreground" />
          </div>

          {linksLoading ? (
            <CardSkeleton lines={3} />
          ) : barItems.length > 0 ? (
            <HorizontalBarChart items={barItems} />
          ) : (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <LinkIcon size={24} className="text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Nenhum link gerado ainda</p>
            </div>
          )}

          {barItems.length > 0 && (
            <Link
              to="/app/links"
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[hsl(var(--border))] px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:border-[hsl(var(--border-strong))] transition-all"
            >
              Ver todos os links
            </Link>
          )}
        </Card>

        {/* Links em Destaque (cards de produtos) - estilo easyfy */}
        <FeaturedLinks
          links={allLinks}
          isLoading={linksLoading}
          className="lg:col-span-2"
        />
      </div>

      {/* BENTO GRID SECUNDARIO: grupos ativos, cliques, ultima conversao */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Grupos ativos */}
        <Card variant="raised" className="p-5 flex flex-col gap-3">
          {statsLoading ? (
            <CardSkeleton />
          ) : (
            <>
              <div className="flex items-start justify-between">
                <CardLabel>Grupos ativos</CardLabel>
                <div className="h-7 w-7 rounded-lg bg-[hsl(var(--primary)/0.12)] flex items-center justify-center">
                  <Users size={14} className="text-[hsl(var(--primary))]" />
                </div>
              </div>
              <p className="text-3xl font-bold text-tabular text-foreground leading-none">
                {fmt(activeGroups)}
              </p>
              {groupsNearLimit > 0 && (
                <p className="text-xs text-[hsl(var(--destructive))]">
                  {groupsNearLimit} perto do limite
                </p>
              )}
              {activeGroups === 0 && (
                <p className="text-xs text-muted-foreground">Nenhum grupo conectado</p>
              )}
              <Link
                to="/app/grupos"
                className="mt-auto inline-flex items-center gap-1 text-xs text-[hsl(var(--primary))] hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[hsl(var(--ring))] rounded"
              >
                Ver grupos
                <ArrowRight size={11} />
              </Link>
            </>
          )}
        </Card>

        {/* Conversoes com sucesso - sparkline */}
        <Card variant="revenue" className="p-5 flex flex-col gap-3 relative overflow-hidden grain-overlay">
          <div
            className="pointer-events-none absolute inset-0 rounded-xl"
            style={{
              background: "radial-gradient(ellipse at 80% 20%, hsl(150 54% 51% / 0.06) 0%, transparent 65%)",
            }}
            aria-hidden="true"
          />

          {statsLoading ? (
            <CardSkeleton />
          ) : (
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <CardLabel>Conversões com sucesso</CardLabel>
                {deltas.successConversions !== undefined && (
                  <DeltaBadge delta={deltas.successConversions} />
                )}
              </div>
              <p className="headline-revenue leading-none mt-2" aria-label={`${successConversions} conversões com sucesso no período`}>
                {fmt(successConversions)}
              </p>

              <div className="mt-3 -mx-1">
                {hasDailySuccess ? (
                  <Sparkline
                    values={dailySuccessConversions}
                    labels={dateLabels}
                    color="hsl(150 54% 51%)"
                    height={36}
                    className="w-full"
                  />
                ) : (
                  <EmptyChart />
                )}
              </div>

              <div className="flex items-center gap-3 pt-2 mt-2 border-t border-[hsl(var(--border))]">
                <span className="flex items-center gap-1 text-xs text-[hsl(var(--primary))]">
                  <CheckCircle2 size={11} aria-hidden="true" />
                  {fmt(statusCounts.success)}
                </span>
                <span className="flex items-center gap-1 text-xs text-[hsl(var(--destructive))]">
                  <XCircle size={11} aria-hidden="true" />
                  {fmt(statusCounts.error)}
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <MinusCircle size={11} aria-hidden="true" />
                  {fmt(statusCounts.skipped)} ignorados
                </span>
              </div>
            </div>
          )}
        </Card>

        {/* Ultima conversao */}
        <Card variant="profit" className="p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <CardLabel>Última conversão</CardLabel>
            <Zap size={13} className="text-[hsl(var(--primary))]" />
          </div>

          {convLoading ? (
            <CardSkeleton />
          ) : lastConversion ? (
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-xl font-bold text-tabular text-foreground leading-none">
                  {lastConversion.status === "success" ? "Sucesso" : lastConversion.status}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">valor financeiro não registrado</p>
              </div>

              <div className="rounded-lg bg-[hsl(var(--surface))] px-3 py-2 flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--primary))] animate-pulse" />
                <span className="text-xs font-medium text-foreground">
                  {mkLabel(lastConversion.marketplace_code)}
                </span>
              </div>

              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock size={11} />
                {timeAgo(lastConversion.created_at)}
              </p>

              <Link
                to="/app/conversoes"
                className="mt-auto inline-flex items-center gap-1 text-xs text-[hsl(var(--primary))] hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[hsl(var(--ring))] rounded"
              >
                Ver todas
                <ArrowRight size={11} />
              </Link>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <div className="h-10 w-10 rounded-full bg-[hsl(var(--primary)/0.1)] flex items-center justify-center">
                <Zap size={18} className="text-[hsl(var(--primary))]" />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Nenhuma conversão registrada ainda
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* EMPTY STATE */}
      {isEmptyState && (
        <Card variant="ghost" className="p-10 text-center space-y-5 border-dashed">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--primary)/0.1)]">
            <Users size={22} className="text-[hsl(var(--primary))]" />
          </div>
          <div className="space-y-1.5 max-w-sm mx-auto">
            <p className="font-semibold text-foreground font-display">
              Conecte seu primeiro grupo para começar a lucrar
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Adicione o bot ao seu grupo do Telegram como administrador.
              Os links de afiliado passarão a ser convertidos automaticamente.
            </p>
          </div>
          <Link
            to="/app/grupos"
            className={cn(
              "inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold",
              "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]",
              "hover:brightness-110 hover:shadow-[var(--shadow-primary)]",
              "transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]",
            )}
          >
            <Users size={15} />
            Conectar grupo
            <ArrowRight size={14} />
          </Link>
        </Card>
      )}
    </div>
  );
}
